// @ts-strict-ignore
import React, { useEffect, useRef, useState } from 'react'

import L, { LocationEvent, Map } from 'leaflet'

import { CollectionItem, ScreenSection, RootStoreState } from 'mytours-read-common'

import '../../../common_lib/maps/MBTiles'

import { formatMapPinIcon, SwitchDirection, switchItemAction } from '../functions'

import { MapContainer, GeoreferencedLayerType, LayerTypeEnum, MapControls, Marker, StaticMapLayerType } from '../types'
import { GetCollectionsShowResponseMap } from 'mytours-read-api-typescript'
import { getDirMode } from '../../platform'
import { createLeafletMarkerIcon, getMBTilesMetadata } from 'common_lib/maps/functions'
import usePrevious from 'common_lib/hooks/use-previous'
import { useSelector } from 'react-redux'
import Loader from 'mobile_web/modules/common/components/Loader'

interface Props extends MapContainer {
	items: CollectionItem[]
	currentItemIndex: number
	mapControls: (params: MapControls) => JSX.Element
	selectedMapLayerId: number
	currentMap: GetCollectionsShowResponseMap
	onSetActiveItem?: (item: CollectionItem | ScreenSection, itemIndex: number) => void
	showInAppDirections?: (showInAppDirections: boolean) => void
	onMapReady?: () => void
	mapType: StaticMapLayerType | GeoreferencedLayerType
	onMapResize?(): void
}

const LeafletMap: React.FC<Props> = props => {
	const [markers, setMarkers] = useState<Marker[]>([])
	const [mapObject, setMapObject] = useState<Map>(null)
	const locationMarker = useRef<L.CircleMarker>()

	const [mapLoaded, setMapLoaded] = useState<boolean>(false)
	const [isLoading, setLoading] = useState<boolean>(false)
	const [mapBounds, setMapBounds] = useState(null)
	const [dbReady, setDbReady] = useState<boolean>(false)

	const appData = useSelector((state: RootStoreState) => state.settings.appMetaData)

	const mapContainer = useRef<HTMLDivElement>()

	const prevPropsSelectedMapLayerId = usePrevious<number>(props.currentItemIndex)
	const prevPropsCurrentItemIndex = usePrevious<number>(props.currentItemIndex)
	const prevMapObject = usePrevious<Map>(mapObject)
	const prevDbReady = usePrevious<boolean>(dbReady)

	useEffect(() => {
		if (!mapContainer.current) return

		const setUpMapCondition =
			props.selectedMapLayerId !== null && prevPropsSelectedMapLayerId !== props.selectedMapLayerId

		if (setUpMapCondition || !mapLoaded) {
			setUpMap(setUpMapCondition)
		}
	}, [mapContainer, props.selectedMapLayerId])

	useEffect(() => {
		if (!mapContainer.current) return

		if (prevPropsCurrentItemIndex !== props.currentItemIndex && props.currentItemIndex != null) {
			setActiveMarkerIcon(props.items[props.currentItemIndex])
		}
	}, [mapContainer, props.currentItemIndex])

	useEffect(() => {
		if (prevMapObject && mapObject === null) {
			setUpMap(true)
		} else if (prevMapObject === null && mapObject) {
			createMarkers()
			createLocationMarker()
		}
	}, [mapObject])

	useEffect(() => {
		if (!prevDbReady && dbReady) {
			resizeMap()
		}
	}, [dbReady])

	useEffect(() => {
		if (markers) {
			setActiveMarkerIcon(props.items[props.currentItemIndex])
		}
	}, [markers])

	const resizeMap = () => {
		let attempts = 0
		const resizeInterval = setInterval(() => {
			if (attempts === 20) {
				clearInterval(resizeInterval)
				return
			}

			mapObject?.invalidateSize()
			props?.onMapResize()
			attempts++
		}, 100)
	}

	const createMarker = (collectionItem: CollectionItem): Promise<Marker> =>
		new Promise(resolve => {
			const { currentItemIndex, items } = props

			const {
				lat,
				lng,
				id,
				map_pin_style: mapPinStyle,
				map_pin_colour: mapPinColour,
				map_layer_id: mapLayerId,
				item_number: itemNumber,
				map_pin_icon: mapPinIcon,
			} = collectionItem

			const isActiveMarker = items[currentItemIndex]?.id == collectionItem.id

			const icon = formatMapPinIcon({
				mapPinIcon,
				itemNumber,
				keypadEnabled: appData.keypad_enabled,
				keypadDigits: appData.keypad_digits,
			})

			// Create new marker, coordinates, and add it to map
			const marker = L.marker([Number(lat), Number(lng)], {
				icon: createLeafletMarkerIcon(mapPinStyle, mapPinColour, isActiveMarker ? 'active' : icon),
			})
				.on('click', bindMarkerOnClick(collectionItem))
				.addTo(mapObject)

			// Add it to end of list
			resolve({
				marker,
				lat,
				lng,
				collectionItemId: id,
				pinColor: mapPinColour,
				pinIcon: icon,
				pinStyle: mapPinStyle,
				mapLayerId,
			})
		})

	// Call prop which opens side panel with marker data
	const bindMarkerOnClick = (collectionItem: CollectionItem) => () => {
		const { items, onSetActiveItem } = props
		const pageIndex: number = items?.findIndex(item => item.id === collectionItem.id)

		setActiveMarkerIcon(collectionItem)

		if (onSetActiveItem) onSetActiveItem(collectionItem, pageIndex)
	}

	const createLocationMarker = () => {
		if (!mapObject || props.mapType !== LayerTypeEnum.Georeferenced) {
			return
		}

		locationMarker.current = L.circleMarker([0, 0], {
			weight: 2,
			color: '#FFF',
			radius: 8,
			fillColor: '#2DA1F2',
			fillOpacity: 1,
		}).addTo(mapObject)
	}

	const formatMbTilesURL = (mapMediaItemFile: string, tileFormat: 'PNG' | 'JPEG' | 'PNG8'): string => {
		if (!mapMediaItemFile || !tileFormat) {
			return ''
		}

		const splitedUrl = mapMediaItemFile.split('/')
		splitedUrl[splitedUrl.length - 1] = 'map.mbtiles'
		splitedUrl[splitedUrl.length - 2] = tileFormat.toLowerCase()

		return splitedUrl.join('/')
	}

	const setUpMap = (condition?: boolean) => {
		if (mapLoaded && !condition) {
			return
		}

		setLoading(true)

		setMapLoaded(true)
		const { currentMap, selectedMapLayerId, mapType, onMapReady } = props

		const currentMapLayer = selectedMapLayerId && currentMap.layers.find(layer => layer.id === selectedMapLayerId)
		const mapMediaItem = currentMapLayer?.map_media_item
		const tileUrl = formatMbTilesURL(mapMediaItem?.file?.original_url, currentMapLayer?.tile_format)

		// Remove existing map
		if (mapObject) {
			mapObject.remove()
			setMarkers([])
			setMapObject(null)
			setDbReady(false)
			return
		}

		;(async () => {
			let mapTmp: L.Map

			if (mapType === LayerTypeEnum.Static) {
				mapTmp = new L.Map(mapContainer.current).fitWorld()
			} else if (mapType === LayerTypeEnum.Georeferenced) {
				const { center, minZoom } = await getMBTilesMetadata(tileUrl)

				if (!center) {
					return
				}

				mapTmp = L.map(mapContainer.current)

				mapTmp.setView(center, minZoom)
			}

			// Set the tiles
			// @ts-expect-error We're extending Leaflet from common_lib/maps/MBTiles.ts
			const mb = L.tileLayer.mbTiles(tileUrl, { noWrap: true }).addTo(mapTmp)
			// Listen to when local DB has loaded
			mb.on('databaseloaded', e => {
				// Set zoom and bounds
				const { minZoom, maxZoom, bounds } = e
				mapTmp.options.minZoom = minZoom
				mapTmp.options.maxZoom = maxZoom

				const zoom = minZoom + Math.round((maxZoom - minZoom) / 2)

				mapTmp.setZoom(zoom > maxZoom ? maxZoom : zoom)

				// reading map bounds from georeferenced map is not working, use markers bounds instead
				if (mapType !== LayerTypeEnum.Georeferenced) {
					setMapBounds(bounds)
				}

				setMapObject(mapTmp)
				// console.info('MBTiles DB loaded', e)

				setDbReady(true)
				onMapReady?.()
				setLoading(false)

				if (mapType === LayerTypeEnum.Georeferenced) {
					mapTmp.locate({ watch: true })

					const onLocationFound = (e: LocationEvent) => {
						locationMarker.current?.setLatLng(e.latlng)
					}

					mapTmp.on('locationfound', onLocationFound)
				}
			})
			mb.on('databaseerror', () => {
				// console.info('MBTiles DB error', e)
				setDbReady(true)
				setLoading(false)
			})
		})()
	}

	const createMarkers = async () => {
		const { items } = props
		const [...markersTmp] = markers
		// @ts-expect-error TODO: `latLngBounds` require argument. Not sure how bounds is calculated without argument
		const bounds = L.latLngBounds()

		for await (const item of items) {
			const hasCoordinates = Boolean(item?.lat && item?.lng)

			if (hasCoordinates) {
				const marker = await createMarker(item)
				bounds.extend((marker.marker as any).getLatLng())
				markersTmp.push(marker)
			}
		}

		// reading map bounds is not working for georeferenced map, using markers bounds instead
		if (props.mapType === LayerTypeEnum.Georeferenced) {
			setMapBounds(bounds)
		}
		setMarkers(markersTmp)
	}

	const setActiveMarkerIcon = (collectionItem?: CollectionItem) => {
		markers.forEach(item => {
			if (item.mapLayerId === props.selectedMapLayerId) {
				if (item.collectionItemId === collectionItem?.id) {
					const { map_pin_colour: mapPinColour, map_pin_style: mapPinStyle } = collectionItem || {}
					;(item.marker as any).setIcon(createLeafletMarkerIcon(mapPinStyle, mapPinColour, 'active'))
				} else {
					const { pinColor: mapPinColour, pinIcon: mapPinIcon, pinStyle: mapPinStyle } = item
					;(item.marker as any).setIcon(createLeafletMarkerIcon(mapPinStyle, mapPinColour, mapPinIcon))
				}
			} else {
				;(item.marker as any).setIcon(
					L.icon({
						iconUrl: '//:0',
						iconSize: [1, 1],
						iconAnchor: [1, 1],
					}),
				)
			}
		})
	}

	const showAllBounds = () => {
		if (mapBounds) {
			mapObject.fitBounds(mapBounds)
		}
	}

	return (
		<div className={getDirMode()} style={{ height: '100%', width: '100%', zIndex: 0 }} ref={mapContainer}>
			{isLoading && <Loader />}
			{props.mapControls?.({
				showAllBounds,
				showInAppDirections: props.showInAppDirections,
			})}
			<span className="hidden" aria-live="assertive">
				{props.items?.[props.currentItemIndex]?.title}
			</span>
		</div>
	)
}

export default LeafletMap
